// Per-incident chat panel — Claude-style transcript matched to the dashboard
// theme: animated thinking state, tool-call cards, streaming markdown with a
// live cursor. User turns are bubbles; assistant turns are full-width prose.

import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useIncidentChat, type ChatMessage } from '../../hooks/useIncidentChat'

function statusLabel(status: string | null): string {
  if (!status) return 'Thinking'
  if (status === 'resolving') return 'Resolving incident'
  if (status === 'cloning') return 'Preparing workspace'
  if (status === 'ready') return 'Thinking'
  if (status.startsWith('tool:')) return `Calling ${status.slice('tool:'.length)}`
  return status
}

// Keyframes can't be expressed inline; inject once. Colors use theme tokens.
const CHAT_STYLE = `
@keyframes ic-blink { 0%,100% { opacity: 1 } 50% { opacity: 0 } }
@keyframes ic-bounce { 0%,80%,100% { transform: translateY(0); opacity: .35 } 40% { transform: translateY(-3px); opacity: 1 } }
@keyframes ic-fade { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
@keyframes ic-spin { to { transform: rotate(360deg) } }
.ic-msg { animation: ic-fade .18s ease-out }
.ic-cursor { display: inline-block; width: 7px; height: 14px; margin-left: 2px; vertical-align: text-bottom;
  background: var(--accent); animation: ic-blink 1s step-end infinite; border-radius: 1px }
.ic-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%;
  background: var(--text-muted); animation: ic-bounce 1.2s infinite ease-in-out }
.ic-spinner { width: 11px; height: 11px; border: 2px solid var(--border);
  border-top-color: var(--accent); border-radius: 50%; animation: ic-spin .7s linear infinite; flex-shrink: 0 }
.ic-prose p { margin: 0 0 8px }
.ic-prose p:last-child { margin-bottom: 0 }
.ic-prose ul, .ic-prose ol { margin: 4px 0 8px; padding-left: 20px; display: flex; flex-direction: column; gap: 3px }
.ic-prose h4 { font-size: 12px; font-weight: 600; color: var(--text-h); text-transform: uppercase;
  letter-spacing: .04em; margin: 10px 0 6px }
.ic-prose code { font-family: var(--mono); font-size: 12px; background: rgba(255,255,255,.06);
  padding: 1px 5px; border-radius: 4px; color: var(--accent-light) }
.ic-prose pre { margin: 6px 0; padding: 10px 12px; border: 1px solid var(--border); border-radius: 6px;
  background: var(--bg-soft); overflow-x: auto }
.ic-prose pre code { background: none; padding: 0; color: var(--text); font-size: 12px; line-height: 1.5 }
.ic-table-wrap { overflow-x: auto; margin: 6px 0 10px }
.ic-prose table { border-collapse: collapse; width: 100%; font-size: 12px }
.ic-prose th, .ic-prose td { border: 1px solid var(--border); padding: 6px 9px; text-align: left;
  vertical-align: top; word-break: break-word }
.ic-prose th { background: var(--bg-soft); color: var(--text-h); font-weight: 600 }
.ic-prose td { color: var(--text) }
.ic-overlay { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center;
  padding: 24px; background: rgba(0,0,0,.55); backdrop-filter: blur(3px); animation: ic-fade .15s ease-out }
.ic-iconbtn { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px;
  border: 1px solid var(--border); border-radius: 6px; background: var(--bg-soft); color: var(--text-muted);
  cursor: pointer; padding: 0; transition: color .15s ease, border-color .15s ease }
.ic-iconbtn:hover { color: var(--text-h); border-color: var(--accent) }
.ic-empty { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; gap: 16px; padding: 24px 8px; animation: ic-fade .2s ease-out }
.ic-empty-badge { width: 44px; height: 44px; border-radius: 12px; display: inline-flex; align-items: center;
  justify-content: center; color: var(--accent); border: 1px solid var(--border); background: var(--bg-soft) }
.ic-empty-title { font-size: 15px; font-weight: 600; color: var(--text-h); margin-bottom: 6px }
.ic-empty-sub { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; max-width: 320px; margin: 0 auto }
.ic-suggests { display: flex; flex-direction: column; gap: 8px; width: 100%; max-width: 340px; margin-top: 4px }
.ic-suggest { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; padding: 9px 12px;
  border: 1px solid var(--border); border-radius: 8px; background: var(--bg-soft); color: var(--text);
  font-size: 12.5px; font-family: inherit; cursor: pointer;
  transition: border-color .15s ease, color .15s ease, background .15s ease }
.ic-suggest:hover:not(:disabled) { border-color: var(--accent); color: var(--text-h); background: rgba(255,255,255,.04) }
.ic-suggest:disabled { opacity: .5; cursor: not-allowed }
.ic-suggest svg { color: var(--text-muted); flex-shrink: 0 }
.ic-suggest:hover:not(:disabled) svg { color: var(--accent) }
`

// ── Minimal, dependency-free markdown rendering (code fences, tables, inline
//    code, bold, headings, lists, paragraphs) so the chat reads like prose.
function parseTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim())
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-') && line.includes('|')
}

function renderInline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    if (tok.startsWith('`')) out.push(<code key={`${key}-c${i}`}>{tok.slice(1, -1)}</code>)
    else out.push(<strong key={`${key}-b${i}`}>{tok.slice(2, -2)}</strong>)
    last = m.index + tok.length
    i += 1
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

function renderTextBlock(text: string, key: string): ReactNode[] {
  const lines = text.split('\n')
  const nodes: ReactNode[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  let n = 0
  const flush = () => {
    if (!list) return
    const items = list.items.map((it, j) => (
      <li key={`${key}-li${n}-${j}`}>{renderInline(it, `${key}-li${n}-${j}`)}</li>
    ))
    nodes.push(list.ordered ? <ol key={`${key}-l${n}`}>{items}</ol> : <ul key={`${key}-l${n}`}>{items}</ul>)
    list = null
    n += 1
  }

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const isRow = /^\s*\|.*\|\s*$/.test(line)

    // GitHub-flavored table: header row + separator + body rows.
    if (isRow && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flush()
      const header = parseTableRow(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(parseTableRow(lines[i]))
        i += 1
      }
      nodes.push(
        <div className="ic-table-wrap" key={`${key}-tbl${i}`}>
          <table>
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th key={hi}>{renderInline(h, `${key}-th${i}-${hi}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td key={ci}>{renderInline(c, `${key}-td${i}-${ri}-${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    const bullet = /^\s*[-*]\s+(.*)/.exec(line)
    const num = /^\s*\d+\.\s+(.*)/.exec(line)
    const head = /^(#{1,6})\s+(.*)/.exec(line)
    if (bullet) {
      if (!list || list.ordered) flush()
      if (!list) list = { ordered: false, items: [] }
      list.items.push(bullet[1])
    } else if (num) {
      if (!list || !list.ordered) flush()
      if (!list) list = { ordered: true, items: [] }
      list.items.push(num[1])
    } else {
      flush()
      if (head) nodes.push(<h4 key={`${key}-h${i}`}>{renderInline(head[2], `${key}-h${i}`)}</h4>)
      else if (line.trim()) nodes.push(<p key={`${key}-p${i}`}>{renderInline(line, `${key}-p${i}`)}</p>)
    }
    i += 1
  }
  flush()
  return nodes
}

function Markdown({ text }: { text: string }) {
  const segments = text.split('```')
  return (
    <div className="ic-prose" style={{ fontSize: '13px', lineHeight: 1.55, color: 'var(--text)' }}>
      {segments.map((seg, idx) => {
        if (idx % 2 === 1) {
          const body = seg.replace(/^[a-zA-Z0-9_-]*\n/, '')
          return (
            <pre key={`code-${idx}`}>
              <code>{body}</code>
            </pre>
          )
        }
        return <Fragment key={`txt-${idx}`}>{renderTextBlock(seg, `s${idx}`)}</Fragment>
      })}
    </div>
  )
}

function ThinkingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
      <span className="ic-dot" style={{ animationDelay: '0s' }} />
      <span className="ic-dot" style={{ animationDelay: '0.15s' }} />
      <span className="ic-dot" style={{ animationDelay: '0.3s' }} />
    </span>
  )
}

function ToolCard({ name, detail, live }: { name: string; detail?: string; live: boolean }) {
  return (
    <div
      className="ic-msg"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        margin: '0 0 8px',
        borderRadius: '6px',
        border: '1px solid var(--border)',
        background: 'var(--bg-soft)',
        fontSize: '12px',
      }}
    >
      {live ? <span className="ic-spinner" /> : <span style={{ color: 'var(--accent)' }}>✓</span>}
      <span style={{ color: 'var(--text-muted)' }}>Tool</span>
      <code style={{ fontFamily: 'var(--mono)', color: 'var(--text-h)', fontWeight: 600 }}>{name}</code>
      {detail && (
        <code
          style={{
            fontFamily: 'var(--mono)',
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {detail}
        </code>
      )}
    </div>
  )
}

const userBubble: CSSProperties = {
  maxWidth: '85%',
  padding: '9px 13px',
  borderRadius: '12px 12px 4px 12px',
  fontSize: '13px',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: 'var(--accent-bg)',
  border: '1px solid var(--border-hover)',
  color: 'var(--text-h)',
}

function AssistantTurn({
  message,
  isStreaming,
  status,
}: {
  message: ChatMessage
  isStreaming: boolean
  status: string | null
}) {
  const tools = message.tools ?? []
  const hasText = message.content.length > 0
  const lastToolLive = isStreaming && !hasText
  return (
    <div className="ic-msg" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {tools.map((t, i) => (
        <ToolCard key={i} name={t.name} detail={t.detail} live={lastToolLive && i === tools.length - 1} />
      ))}
      {hasText ? (
        <div style={{ width: '100%' }}>
          <Markdown text={message.content} />
          {isStreaming && <span className="ic-cursor" />}
        </div>
      ) : (
        isStreaming &&
        tools.length === 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'var(--text-muted)',
              fontSize: '13px',
              fontStyle: 'italic',
            }}
          >
            {statusLabel(status)} <ThinkingDots />
          </div>
        )
      )}
    </div>
  )
}

// Starter prompts shown in the empty state. Scoped to what the agent can
// actually answer (root cause, evidence, fix) — clicking one sends it.
const CHAT_SUGGESTIONS = [
  'Summarize the root cause in plain terms',
  'What evidence supports this conclusion?',
  'Walk me through the proposed fix',
]

function ExpandIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {expanded ? (
        // arrows pointing in (collapse)
        <>
          <path d="M9 9 4 4M9 9v-4M9 9H5" />
          <path d="M15 15 20 20M15 15v4M15 15h4" />
        </>
      ) : (
        // arrows pointing out (expand)
        <>
          <path d="M15 4h5v5M20 4l-6 6" />
          <path d="M9 20H4v-5M4 20l6-6" />
        </>
      )}
    </svg>
  )
}

export default function IncidentChat({ investigationId }: { investigationId: string }) {
  const { messages, status, streaming, streamingId, error, send } = useIncidentChat(investigationId)
  const [draft, setDraft] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow the input with its content (one line up to a cap) so text never
  // clips; runs on every draft change, including when cleared after send.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [draft])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, status])

  useEffect(() => {
    // On expand/collapse the transcript resizes; smoothly re-pin to the latest
    // message so the newest turn is in view.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [isExpanded])

  // In full-screen mode, Escape collapses back to the sidebar.
  useEffect(() => {
    if (!isExpanded) return
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setIsExpanded(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isExpanded])

  function submit() {
    const text = draft
    if (!text.trim() || streaming) return
    setDraft('')
    void send(text)
  }
  function onSubmit(e: FormEvent) {
    e.preventDefault()
    submit()
  }
  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const card = (
    <div
      className="summary-card"
      style={
        isExpanded
          ? {
              padding: 0,
              overflow: 'hidden',
              gap: 0,
              width: 'min(1100px, 94vw)',
              height: '90vh',
              // Solid opaque background so the page behind doesn't bleed through
              // the translucent glass card; backdrop stays translucent.
              background: 'var(--bg)',
            }
          : { padding: 0, overflow: 'hidden', height: '560px', gap: 0 }
      }
      onMouseDown={(e) => e.stopPropagation()}
    >
      <style>{CHAT_STYLE}</style>

      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text-h)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        Ask about this incident
        <span
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {streaming && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: 0,
                color: 'var(--text-muted)',
                display: 'inline-flex',
                gap: '6px',
                alignItems: 'center',
              }}
            >
              <span className="ic-spinner" /> {statusLabel(status)}
            </span>
          )}
          <button
            type="button"
            className="ic-iconbtn"
            onClick={() => setIsExpanded((v) => !v)}
            title={isExpanded ? 'Collapse chat' : 'Expand to full screen'}
            aria-label={isExpanded ? 'Collapse chat' : 'Expand to full screen'}
          >
            <ExpandIcon expanded={isExpanded} />
          </button>
        </span>
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 18px' }}>
        {messages.length === 0 && (
          <div className="ic-empty">
            <span className="ic-empty-badge">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <div>
              <div className="ic-empty-title">Ask about this incident</div>
              <p className="ic-empty-sub">
                The agent has the full investigation context and can read the affected code.
                Ask about the root cause, the evidence, or the applied fix.
              </p>
            </div>
            <div className="ic-suggests">
              {CHAT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="ic-suggest"
                  disabled={streaming}
                  onClick={() => void send(s)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.role === 'user' ? (
            <div
              key={m.id}
              className="ic-msg"
              style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}
            >
              <div style={userBubble}>{m.content}</div>
            </div>
          ) : (
            <div key={m.id} style={{ marginBottom: '16px' }}>
              <AssistantTurn message={m} isStreaming={streamingId === m.id} status={status} />
            </div>
          ),
        )}
        {error && (
          <div style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{error}</div>
        )}
      </div>

      <form
        onSubmit={onSubmit}
        style={{
          display: 'flex',
          gap: '8px',
          padding: '12px',
          borderTop: '1px solid var(--border)',
          alignItems: 'flex-end',
        }}
      >
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about this incident…"
          rows={1}
          disabled={streaming}
          style={{
            flex: 1,
            resize: 'none',
            minHeight: '40px',
            maxHeight: '120px',
            boxSizing: 'border-box',
            padding: '10px 12px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            background: 'var(--bg-soft)',
            color: 'var(--text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            lineHeight: 1.5,
            outline: 'none',
            overflowY: 'auto',
          }}
        />
        <button
          type="submit"
          className="btn-coral-cta"
          disabled={streaming || !draft.trim()}
          style={{ whiteSpace: 'nowrap' }}
        >
          {streaming ? '…' : 'Send'}
        </button>
      </form>
    </div>
  )

  if (isExpanded) {
    // Click on the dimmed backdrop (outside the card) collapses the chat.
    return (
      <div className="ic-overlay" onMouseDown={() => setIsExpanded(false)}>
        {card}
      </div>
    )
  }

  return card
}
